import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Card, Button, Table, Badge, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useToast } from 'context/providers/ToastProvider';

const CleanCopyButton = ({ text, label = "Copiar" }) => {
    const { showSuccessToast, showErrorToast } = useToast();
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            showSuccessToast('Copiado: ' + text);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            showErrorToast('Error al copiar');
        }
    };

    return (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-copy-${text}`}>{copied ? '¡Copiado!' : label}</Tooltip>}
        >
            <Button
                variant="link"
                size="sm"
                className="p-0 text-secondary ms-2"
                onClick={handleCopy}
                style={{ fontSize: '0.85rem' }}
            >
                <FontAwesomeIcon icon={copied ? "check" : "copy"} className={copied ? "text-success" : ""} />
            </Button>
        </OverlayTrigger>
    );
};

CleanCopyButton.propTypes = {
    text: PropTypes.string.isRequired,
    label: PropTypes.string
};

const UsuariosAkasiaPOS = ({ usuarios, loading, loadingMore, hasMoreData, cargarMasDatos, licencias, onNuevoUsuario, onEditarUsuario, onCrearSuperAdmin }) => {
    const resolveRoleName = (usuario) => usuario.vRolName || usuario.vNombreRol || usuario.vRoleName || (usuario.bIsSuperAdmin ? 'Super Admin' : 'Administrador');

    const resolveFullName = (usuario) => {
        if (usuario.vNombreCompleto) return usuario.vNombreCompleto;
        const first = (usuario.vFirstName || usuario.vNombre || '').trim();
        const last = (usuario.vLastName || usuario.vApellido || '').trim();
        const fullName = `${first} ${last}`.trim();
        return fullName || '--';
    };

    const formatDate = (d) => {
        if (!d) return '--';
        try {
            const dt = new Date(d);
            if (Number.isNaN(dt.getTime())) return d;
            const day = dt.getDate().toString().padStart(2, '0');
            const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
            return `${day} ${months[dt.getMonth()]} ${dt.getFullYear()}`;
        } catch {
            return d;
        }
    };

    const getStatusBadge = (isSuperAdmin, expirationDate) => {
        if (isSuperAdmin) return { label: 'Activo', bg: 'success' };
        if (!expirationDate) return { label: 'Activo', bg: 'success' };

        try {
            const diffDays = Math.ceil((new Date(expirationDate) - new Date()) / (1000 * 60 * 60 * 24));
            if (diffDays < 0) return { label: 'Vencido', bg: 'danger' };
            if (diffDays <= 7) return { label: 'Por Vencer', bg: 'warning', text: 'dark' };
            return { label: 'Activo', bg: 'success' };
        } catch {
            return { label: 'Activo', bg: 'success' };
        }
    };

    /**
     * Componente para el trigger de infinite scroll
     */
    const InfiniteScrollTrigger = () => {
        const triggerRef = useRef();

        useEffect(() => {
            const observer = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && hasMoreData && !loadingMore && !loading) {
                        if (cargarMasDatos) cargarMasDatos();
                    }
                },
                { threshold: 0.1 }
            );

            if (triggerRef.current) {
                observer.observe(triggerRef.current);
            }

            return () => {
                if (triggerRef.current) {
                    observer.unobserve(triggerRef.current);
                }
            };
        }, [hasMoreData, loadingMore, loading, cargarMasDatos]);

        if (!hasMoreData) return null;

        return (
            <div ref={triggerRef} className="text-center py-4">
                {loadingMore ? (
                    <div>
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <div className="text-muted small">Cargando más usuarios...</div>
                    </div>
                ) : (
                    <div className="text-muted small">Desplázate para cargar más</div>
                )}
            </div>
        );
    };

    return (
        <Card className="mb-3 border-0 shadow-sm">
            <Card.Header className="bg-light d-flex justify-content-between align-items-center py-3">
                <h5 className="mb-0">
                    <FontAwesomeIcon icon="user-tag" className="me-1" />
                    Usuarios Akasia POS</h5>

                <div className="d-flex gap-2">
                    {usuarios.length === 0 && licencias?.bIsActive && (
                        <Button onClick={onCrearSuperAdmin} variant="primary" size="sm">
                            <FontAwesomeIcon icon="shield-alt" className="me-1" />
                            Crear Super Admin
                        </Button>
                    )}
                    {usuarios.length > 0 && licencias?.bIsActive && !licencias?.bIsPeriodoPrueba && (
                        <Button onClick={onNuevoUsuario} variant="primary" size="sm">
                            <FontAwesomeIcon icon="plus" className="me-1" />
                            Nuevo Usuario
                        </Button>
                    )}
                </div>
            </Card.Header>

            <Card.Body className="p-0">
                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                        <span className="ms-2 text-muted small">Cargando usuarios...</span>
                    </div>
                ) : usuarios.length === 0 ? (
                    <div className="text-center py-5">
                        <p className="text-muted mb-0">No hay usuarios registrados en esta cuenta.</p>
                    </div>
                ) : (
                    <>
                        {/* Vista Escritorio / Tablet (Tabla) */}
                        <div className="d-none d-md-block table-responsive" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                            <Table hover className="mb-0 align-middle small" style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                                <thead className="bg-200 text-900">
                                    <tr>
                                        <th className="border-0">Usuario</th>
                                        <th className="border-0">Nombre</th>
                                        <th className="border-0">Credenciales</th>
                                        <th className="border-0 text-center">Rol</th>
                                        <th className="border-0 text-center">Registro</th>
                                        <th className="border-0 text-center">Vencimiento</th>
                                        <th className="border-0 text-center">Estado</th>
                                        <th className="border-0 text-end pe-4">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usuarios.map(u => {
                                        const status = getStatusBadge(u.bIsSuperAdmin, u.dFinishDate || u.dExpirationDate || u.dUpdatedDate);
                                        const username = u.vUsername || u.vUUIDPosUser || u.idPosUser || '--';
                                        const email = u.vEmail || '--';
                                        const fullName = resolveFullName(u);

                                        return (
                                            <tr key={u.idPosUser || u.idUsuarioAkasiaPos || u.idUsuarioNubeAkasia}>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div className="avatar avatar-xl me-2">
                                                            <img className="rounded-circle" src="/img/usuario20.png" alt="Avatar" />
                                                        </div>
                                                        <h6 className="mb-0 text-dark">{username}</h6>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="fw-semibold text-dark">{fullName}</span>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column gap-1">
                                                        <div>
                                                            <FontAwesomeIcon icon="envelope" className="me-1 text-500" style={{ width: '14px' }} />
                                                            {email}
                                                            {u.vEmail && <CleanCopyButton text={u.vEmail} />}
                                                        </div>
                                                        {u.vPassword && (
                                                            <div>
                                                                <FontAwesomeIcon icon="key" className="me-1 text-500" style={{ width: '14px' }} />
                                                                {u.vPassword}
                                                                <CleanCopyButton text={u.vPassword} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <Badge bg={u.bIsSuperAdmin ? "primary" : "secondary"} className="fw-normal rounded-pill">
                                                        {resolveRoleName(u)}
                                                    </Badge>
                                                </td>
                                                <td className="text-center">{formatDate(u.dCreatedDate || u.dStartDate)}</td>
                                                <td className="text-center">{u.bIsSuperAdmin ? '--' : formatDate(u.dFinishDate || u.dExpirationDate || u.dUpdatedDate)}</td>
                                                <td className="text-center">
                                                    <Badge bg={status.bg} text={status.text || "light"} className="fw-normal rounded-pill">
                                                        {status.label}
                                                    </Badge>
                                                </td>
                                                <td className="text-end pe-4">
                                                    <Button variant="light" size="sm" onClick={() => onEditarUsuario(u)} className="btn-reveal rounded-pill shadow-none">
                                                        <FontAwesomeIcon icon="edit" className="text-primary" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>

                            {usuarios.length > 0 && <InfiniteScrollTrigger />}
                        </div>

                        {/* Vista Móvil (Tarjetas Compactas) */}
                        <div className="d-md-none p-2 bg-light" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <div className="d-flex flex-column gap-2">
                                {usuarios.map(u => {
                                    const status = getStatusBadge(u.bIsSuperAdmin, u.dFinishDate || u.dExpirationDate || u.dUpdatedDate);
                                    const username = u.vUsername || u.vUUIDPosUser || u.idPosUser || '--';
                                    const email = u.vEmail || '--';
                                    const fullName = resolveFullName(u);

                                    return (
                                        <div key={u.idPosUser || u.idUsuarioAkasiaPos || u.idUsuarioNubeAkasia} className="border rounded-2 p-2 bg-white shadow-sm">
                                            <div className="d-flex justify-content-between align-items-center mb-1 pb-1 border-bottom">
                                                <div className="d-flex align-items-center gap-2">
                                                    <img className="rounded-circle" src="/img/usuario20.png" alt="Avatar" style={{ width: '26px', height: '26px' }} />
                                                    <div>
                                                        <span className="fw-bold text-dark small">{username}</span>
                                                        {fullName !== '--' && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{fullName}</div>}
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center gap-1">
                                                    <Badge bg={status.bg} text={status.text || "light"} style={{ fontSize: '0.68rem' }}>
                                                        {status.label}
                                                    </Badge>
                                                    <Button variant="light" size="sm" onClick={() => onEditarUsuario(u)} className="py-0 px-1 border-0 bg-transparent text-primary" title="Editar Usuario">
                                                        <FontAwesomeIcon icon="edit" style={{ fontSize: '0.8rem' }} />
                                                    </Button>
                                                </div>
                                            </div>

                                            <div className="d-flex flex-column gap-1 text-secondary" style={{ fontSize: '0.78rem' }}>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <FontAwesomeIcon icon="envelope" className="me-1 text-muted" style={{ width: '12px' }} />
                                                        <span>{email}</span>
                                                        {u.vEmail && <CleanCopyButton text={u.vEmail} />}
                                                    </div>
                                                    <Badge bg={u.bIsSuperAdmin ? "primary" : "secondary"} className="fw-normal" style={{ fontSize: '0.68rem' }}>
                                                        {resolveRoleName(u)}
                                                    </Badge>
                                                </div>

                                                {u.vPassword && (
                                                    <div>
                                                        <FontAwesomeIcon icon="key" className="me-1 text-muted" style={{ width: '12px' }} />
                                                        <span>{u.vPassword}</span>
                                                        <CleanCopyButton text={u.vPassword} />
                                                    </div>
                                                )}

                                                <div className="d-flex justify-content-between pt-1 border-top mt-1 text-muted" style={{ fontSize: '0.72rem' }}>
                                                    <span><strong>Registro:</strong> {formatDate(u.dCreatedDate || u.dStartDate)}</span>
                                                    <span><strong>Vence:</strong> {u.bIsSuperAdmin ? '--' : formatDate(u.dFinishDate || u.dExpirationDate || u.dUpdatedDate)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {usuarios.length > 0 && <InfiniteScrollTrigger />}
                        </div>
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

UsuariosAkasiaPOS.propTypes = {
    usuarios: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
    loadingMore: PropTypes.bool,
    hasMoreData: PropTypes.bool,
    cargarMasDatos: PropTypes.func,
    licencias: PropTypes.object,
    onNuevoUsuario: PropTypes.func.isRequired,
    onEditarUsuario: PropTypes.func.isRequired,
    onCrearSuperAdmin: PropTypes.func.isRequired
};

export default UsuariosAkasiaPOS;
